import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, tablePath } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function SubsetDialog({ open, onClose, db, table, base, filters, total, recordIds, onCreated }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const count = recordIds ? recordIds.length : total;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/subset`, { name, ...(recordIds ? { record_ids: recordIds } : filters) });
      toast.success(`Created "${data.name}" with ${data.tables[0].row_count} records`);
      setName("");
      onCreated(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-card" data-testid="subset-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">New sub database</DialogTitle>
          <DialogDescription>Copies the {count} {recordIds ? "selected " : ""}record{count === 1 ? "" : "s"} {recordIds ? "" : `currently shown in ${table.name} (after your search and date filter) `}into a new database. Give it to a tech in the Admin panel.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Input required autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={`e.g. ${db.name} - North route`} data-testid="subset-name-input" />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy || !count} data-testid="subset-create-button">{busy && <Loader2 size={14} className="animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CopyRecordDialog({ records, onClose, databases, dbId, base, onCopied }) {  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const others = databases.filter((d) => d.id !== dbId);
  const n = records?.length || 0;
  const copy = async () => {
    setBusy(true);
    let ok = 0;
    let last = null;
    for (const r of records) {
      try {
        last = (await api.post(`${base}/records/${r.id}/copy`, { target_db_id: target })).data;
        ok += 1;
      } catch (err) {
        toast.error(formatError(err));
        break;
      }
    }
    if (last) toast.success(n === 1 ? `Copied to ${last.target} › ${last.table} (${last.fields_copied} fields)${last.created_table ? " — new table created" : ""}`
      : `Copied ${ok} of ${n} records to ${last.target} › ${last.table}`);
    setBusy(false);
    if (ok) { setTarget(""); onCopied(); }
  };
  return (
    <Dialog open={n > 0} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-card" data-testid="copy-record-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Copy to another database</DialogTitle>
          <DialogDescription>Adds a copy of {n === 1 ? "this record" : `these ${n} records`} to the database you pick. If it has no table with this name, one is created.</DialogDescription>
        </DialogHeader>
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger data-testid="copy-target-select"><SelectValue placeholder={others.length ? "Pick a database" : "No other databases"} /></SelectTrigger>
          <SelectContent className="bg-card">{others.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`copy-target-${d.id}`}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={copy} disabled={busy || !target} data-testid="copy-record-confirm">{busy && <Loader2 size={14} className="animate-spin" />} Copy</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Pull records FROM another database/table INTO the current table. Matching field
// names are copied; fields the current table doesn't have are dropped.
export function ImportRecordsDialog({ open, onClose, databases, dbId, table, onImported }) {
  const others = databases.filter((d) => d.id !== dbId);
  const [srcDb, setSrcDb] = useState("");
  const [srcTable, setSrcTable] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const srcDbObj = others.find((d) => d.id === srcDb);
  const tables = srcDbObj?.tables || [];

  const reset = () => { setSrcDb(""); setSrcTable(""); setRows(null); setQ(""); setPicked(new Set()); };
  const close = () => { reset(); onClose(); };

  const pickDb = (id) => { setSrcDb(id); setSrcTable(""); setRows(null); setPicked(new Set()); };
  const pickTable = (name) => { setSrcTable(name); setPicked(new Set()); loadRows(name, ""); };

  const loadRows = async (tname, search) => {
    setLoading(true);
    try {
      const { data } = await api.get(`${tablePath(srcDb, tname)}/records`, { params: { page_size: 50, search: search || undefined } });
      setRows(data);
    } catch (e) {
      toast.error(formatError(e));
      setRows({ records: [], columns: [], total: 0 });
    } finally {
      setLoading(false);
    }
  };

  const label = (rec, cols) => cols.slice(0, 3).map((c) => rec.data[c.name]).filter((v) => v !== null && v !== undefined && v !== "").join(" · ") || "(empty)";
  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOn = !!rows?.records?.length && picked.size === rows.records.length;
  const toggleAll = () => setPicked(allOn ? new Set() : new Set(rows.records.map((r) => r.id)));

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${tablePath(dbId, table.name)}/records/import`, { source_db_id: srcDb, source_table: srcTable, record_ids: [...picked] });
      toast.success(`Added ${data.imported} record${data.imported === 1 ? "" : "s"} to ${table.name}${data.skipped ? ` (${data.skipped} had no matching fields)` : ""}`);
      close();
      onImported();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-xl bg-card max-h-[90vh] overflow-y-auto" data-testid="import-records-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add records from another database</DialogTitle>
          <DialogDescription>Pick a database and table, choose the records, and they're copied into <span className="font-medium text-foreground">{table.name}</span>. Only fields this table also has are brought over.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">From database</p>
              <Select value={srcDb} onValueChange={pickDb}>
                <SelectTrigger data-testid="import-source-db"><SelectValue placeholder={others.length ? "Pick a database" : "No other databases"} /></SelectTrigger>
                <SelectContent className="bg-card">{others.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`import-db-${d.id}`}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Table</p>
              <Select value={srcTable} onValueChange={pickTable} disabled={!srcDb}>
                <SelectTrigger data-testid="import-source-table"><SelectValue placeholder="Pick a table" /></SelectTrigger>
                <SelectContent className="bg-card">{tables.map((t) => <SelectItem key={t.name} value={t.name} data-testid={`import-table-${t.name.replace(/\s+/g, "-")}`}>{t.name} ({t.row_count})</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {srcTable && (
            <div className="space-y-2">
              <Input value={q} onChange={(e) => { setQ(e.target.value); loadRows(srcTable, e.target.value); }} placeholder="Search records…" className="h-8" data-testid="import-search" />
              <div className="border rounded-lg max-h-[300px] overflow-y-auto" data-testid="import-record-list">
                {loading ? (
                  <div className="flex justify-center py-10"><Loader2 className="animate-spin text-primary" /></div>
                ) : rows?.records?.length ? (
                  <>
                    <label className="flex items-center gap-2 border-b px-3 py-2 text-sm font-medium cursor-pointer select-none">
                      <Checkbox checked={allOn ? true : picked.size ? "indeterminate" : false} onCheckedChange={toggleAll} data-testid="import-select-all" />
                      Select all {rows.records.length} shown{rows.total > rows.records.length ? ` (of ${rows.total})` : ""}
                    </label>
                    {rows.records.map((r, i) => (
                      <label key={r.id} className="flex items-center gap-2 px-3 py-2 text-sm border-b last:border-0 hover:bg-secondary/40 cursor-pointer" data-testid={`import-row-${i}`}>
                        <Checkbox checked={picked.has(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`import-pick-${i}`} />
                        <span className="truncate">{label(r, rows.columns)}</span>
                      </label>
                    ))}
                  </>
                ) : (
                  <p className="py-8 text-center text-sm text-muted-foreground" data-testid="import-no-records">No records found.</p>
                )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <Button onClick={run} disabled={busy || !picked.size} data-testid="import-confirm">{busy && <Loader2 size={14} className="animate-spin" />} Add {picked.size || ""} record{picked.size === 1 ? "" : "s"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
